import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import { eventDetailDtoSchema, type EventDetailDto } from '@lib/dto/event.dto'
import { planRevisionListDtoSchema, type PlanRevisionListDto } from '@lib/dto/plan-revision.dto'

/**
 * The earlier versions the database keeps of plans, and putting one back — the
 * web side of the API's `PlanHistoryController`. See `docs/change-tracking.md`.
 */
class PlanHistoryService {
  /** The earlier versions kept of one plan. */
  async revisions(eventId: string, phaseId: string): Promise<PlanRevisionListDto> {
    try {
      return await api.get(`/events/${eventId}/phases/${phaseId}/revisions`, planRevisionListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the earlier versions of a plan', { eventId, phaseId }, error)
      throw error
    }
  }

  /** The plans of this event that were deleted and are still kept. */
  async deletedPlans(eventId: string): Promise<PlanRevisionListDto> {
    try {
      return await api.get(`/events/${eventId}/deleted-plans`, planRevisionListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the deleted plans', { eventId }, error)
      throw error
    }
  }

  /** Put a kept version back: over its plan, or as a new plan when it was deleted. */
  async restore(eventId: string, revisionId: string): Promise<EventDetailDto> {
    try {
      return await api.post(`/events/${eventId}/revisions/${revisionId}/restore`, {}, eventDetailDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to put a plan back', { eventId, revisionId }, error)
      throw error
    }
  }
}

export const planHistoryService = new PlanHistoryService()
